import { el } from '../../core/dom.js';
import { api } from '../../core/api.js';
import { button } from '../../components/button.js';
import { errorPanel } from '../../components/error.js';
import { skeleton } from '../../components/skeleton.js';
import { toast } from '../../components/toast.js';
import { statusNote } from './status.js';

const cache = new Map();
const inflight = new Map();

function rowKey(row) {
  return `${row.source}\0${row.raw_name}`;
}

async function fetchLabels(productId) {
  if (inflight.has(productId)) return inflight.get(productId);
  const task = api(`/event-labels?product_id=${encodeURIComponent(productId)}`)
    .finally(() => inflight.delete(productId));
  inflight.set(productId, task);
  return task;
}

function remember(productId, status, rows, error) {
  cache.set(productId, { status, rows, error });
}

/**
 * Event names for one product. Members can read; admins get an inline edit.
 * @param {{ productId: string, canEdit: boolean }} opts
 */
export function renderEvents(opts) {
  const root = el('section', { class: 'kb-events kb-rv' });
  const cached = cache.get(opts.productId);
  const state = {
    status: cached ? cached.status : 'loading',
    rows: cached ? cached.rows.slice() : [],
    error: cached ? cached.error : '',
    editing: '',
    saving: false,
  };
  paint();
  if (!cached || cached.status !== 'ready') load();
  return root;

  function paint() {
    root.replaceChildren(heading(), body());
  }

  function heading() {
    return el('div', { class: 'kb-mem-title' },
      el('span', { class: 'kb-kicker' }, 'Event names'),
      el('p', { class: 'kb-note' }, 'The name people see. Queries still use the raw event name.'));
  }

  function body() {
    if (state.status === 'loading') return skeleton(4);
    if (state.status === 'error') {
      return errorPanel({
        title: 'Event names could not be loaded',
        message: state.error || 'Try again.',
        action: button('Retry', { onClick: load }),
      });
    }
    if (!state.rows.length) {
      return statusNote('No event names yet', 'Names appear after analytics events are synced.');
    }
    return el('div', { class: 'kb-event-list' }, state.rows.map(renderRow));
  }

  function renderRow(row) {
    const editing = state.editing === rowKey(row);
    return el('div', { class: 'kb-event' },
      el('code', { class: 'kb-event-raw' }, row.raw_name),
      editing ? editForm(row) : labelCell(row),
      el('span', { class: 'kb-event-source' }, row.source || ''),
      editing || !opts.canEdit ? null : el('button', {
        class: 'kb-btn',
        type: 'button',
        onClick() {
          state.editing = rowKey(row);
          paint();
        },
      }, 'Edit'));
  }

  function labelCell(row) {
    return el('div', { class: 'kb-event-label' },
      el('span', null, row.label || ''),
      row.description ? el('span', { class: 'kb-note' }, row.description) : null);
  }

  function editForm(row) {
    return el('form', {
      class: 'kb-event-edit',
      onSubmit(event) {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        save(row, String(data.get('label') || ''), String(data.get('description') || ''));
      },
    },
    el('input', {
      class: 'kb-search',
      name: 'label',
      value: row.label || '',
      maxlength: '80',
      required: true,
      'aria-label': `Label for ${row.raw_name}`,
    }),
    el('input', {
      class: 'kb-search',
      name: 'description',
      value: row.description || '',
      maxlength: '140',
      'aria-label': `Description for ${row.raw_name}`,
    }),
    el('button', {
      class: 'kb-btn kb-btn-fill',
      type: 'submit',
      disabled: state.saving ? true : null,
    }, 'Save'),
    el('button', {
      class: 'kb-btn',
      type: 'button',
      onClick() {
        state.editing = '';
        paint();
      },
    }, 'Cancel'));
  }

  async function load() {
    state.status = 'loading';
    state.error = '';
    paint();
    const data = await fetchLabels(opts.productId);
    if (!root.isConnected) return;
    if (!data || data.error) {
      state.status = 'error';
      state.error = (data && data.error) || 'Could not load event names';
      remember(opts.productId, 'error', [], state.error);
      paint();
      return;
    }
    state.rows = data.labels || [];
    state.status = 'ready';
    remember(opts.productId, 'ready', state.rows, '');
    paint();
  }

  async function save(row, label, description) {
    if (state.saving) return;
    state.saving = true;
    paint();
    const result = await api('/event-labels', {
      method: 'PUT',
      body: {
        product_id: opts.productId,
        source: row.source,
        raw_name: row.raw_name,
        label,
        description: description || undefined,
      },
    });
    state.saving = false;
    if (!root.isConnected) return;
    if (!result || result.error) {
      toast((result && result.error) || 'Could not save the label', 'error');
      paint();
      return;
    }
    const next = state.rows.map((item) => (
      rowKey(item) === rowKey(row) ? { ...item, ...result } : item
    ));
    state.rows = next;
    state.editing = '';
    remember(opts.productId, 'ready', next, '');
    toast('Event name saved');
    paint();
  }
}
